import {
  type VortexOrganizationMemberFunctionReferences,
  VortexOrganizationMembersSurface,
  vortexOrganizationRoleTemplates,
} from "@vortexnyc/vortex-core/auth/react";

import {
  organizationsInviteMember,
  organizationsListMembers,
  organizationsReactivateMember,
  organizationsSetMemberRole,
  organizationsSuspendMember,
} from "~/api-refs";
import { orgMembersClassNames } from "~/integrations/vortex-auth/class-names";
import { useActiveOrganizationVortexId } from "~/integrations/vortex-auth/hooks";
import { logger } from "~/lib/logger";
import { trackEvent } from "~/lib/posthog";

import { Badge } from "../ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";

// F4: Member CRUD is owned by the vortex-auth members surface. The surface
// renders the invite form, member list, role-change select, and
// suspend/reactivate actions inline — so the legacy route-based invite /
// change-role / remove modals are gone.
const organizationMemberRefs = {
  inviteMember: organizationsInviteMember,
  listMembers: organizationsListMembers,
  reactivateMember: organizationsReactivateMember,
  setMemberRole: organizationsSetMemberRole,
  suspendMember: organizationsSuspendMember,
} satisfies VortexOrganizationMemberFunctionReferences;

function getMemberErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

function memberStatusBadgeVariant(
  status: string
): "default" | "destructive" | "secondary" {
  if (status === "active") {
    return "default";
  }
  if (status === "suspended" || status === "inactive") {
    return "destructive";
  }
  return "secondary";
}

export function OrganizationMembers() {
  // The surface passes `organizationId` into inviteMember, which authorizes
  // against `org.vortexOrgId` — so we must hand it the vortexOrgId, not the
  // legacy organizationId string.
  const { vortexOrgId } = useActiveOrganizationVortexId();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Members</CardTitle>
        <CardDescription>
          Manage organization members and their roles
        </CardDescription>
      </CardHeader>
      <CardContent>
        <VortexOrganizationMembersSurface
          captureEvent={(name, properties) => {
            trackEvent(name, properties);
          }}
          classNames={orgMembersClassNames}
          defaultInviteRoleTemplate="member"
          getErrorMessage={getMemberErrorMessage}
          organizationId={vortexOrgId ?? undefined}
          refs={organizationMemberRefs}
          renderActionError={(message) => {
            logger.error("organization_member_action_failed", { message });
            return null;
          }}
          renderStatus={(status) => (
            <Badge variant={memberStatusBadgeVariant(status)}>{status}</Badge>
          )}
          roleOptions={vortexOrganizationRoleTemplates}
        />
      </CardContent>
    </Card>
  );
}
